import React, { Fragment, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { Button, InputGroup, Form } from "react-bootstrap";
import {
  MARKETPLACE_CONSTANTS,
} from "../../../config/constants";
import { colors } from "../../../config/colors";
import { Typography } from "@mui/material";
import { BORDER_SECONDARY, TEXT_ALIGN_LEFT } from "../../../config/styles";
import { ToggleButton } from "../../../components";
import { StockManagement } from "./components";
import { isEmpty } from "lodash";

// Action types
const ACTION_TYPES = {
  SET_FIELD: "SET_FIELD",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  RESET_FORM: "RESET_FORM",
};

// Initial state
const initialState = {
  variantId: "",
  variantPrice: "",
  variantForm: "",
  title: "",
  description: "",
  titleAR: "",
  descriptionAR: "",
  hasError: {},
  vatEnabled: true,
  defaultVariant: true,
  enabledVariant: true,
  variantName: "",
  variantCode: "",
  // Stock Management fields
  stockControlEnabled: false,
  availableStock: "",
  purchaseLimitEnabled: false,
  maxQuantityPerStudent: "",
  vatPercentage: "",
};

// Reducer function
const variantsReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_FIELD:
      return {
        ...state,
        [action.fieldName]: action.value,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null, // Clear error for this field
        },
      };

    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: action.error,
        },
      };

    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        hasError: {
          ...state.hasError,
          [action.fieldName]: null,
        },
      };

    case ACTION_TYPES.RESET_FORM:
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

const Variants = ({
  initialValues,
  onSave = () => { },
  onDelete = () => { },
  variantList = [],
  currency = "AED",
  formList = [],
}) => {
  const [state, dispatch] = useReducer(variantsReducer, initialState);

  const handleSave = () => {
    const { variantPrice, title, titleAR, variantCode, variantName, stockControlEnabled, availableStock, purchaseLimitEnabled, maxQuantityPerStudent, vatPercentage } = state;
    
    // Basic validation
    if (!variantPrice || !title || !titleAR || !variantCode || !variantName) {
      !variantPrice &&
        setError("variantPrice", "Please enter the Variant Price.");
      !title && setError("title", "Please enter the Title.");
      !titleAR && setError("titleAR", "Please enter the Arabic Title.");
      !variantCode && setError('variantCode', 'Please enter the Variant code');
      !variantName && setError('variantName', 'Please enter the Variant Name');
      stockControlEnabled && !availableStock &&
        setError("availableStock", "Please enter the Quantity Per Product.");
      purchaseLimitEnabled && !maxQuantityPerStudent &&
        setError("maxQuantityPerStudent", "Please enter the Maximum Quantity Per Student.");
      !vatPercentage &&
        setError("vatPercentage", "Please enter the VAT Percentage.");  
      return;
    }

    onSave({
      variantId: state.variantId,
      price: state.variantPrice,
      formName: state.variantForm,
      title: state.title,
      description: state.description,
      titleAr: state.titleAR,
      descriptionAr: state.descriptionAR,
      priceInclusiveOfVat: state.vatEnabled,
      defaultVariant: state.defaultVariant,
      enabled: state.enabledVariant,
      variantCode: state.variantCode,
      variantName: state.variantName,
      // Stock management fields
      stockControlEnabled: state.stockControlEnabled,
      availableStock: state.availableStock,
      purchaseLimitEnabled: state.purchaseLimitEnabled,
      maxQuantityPerStudent: state.maxQuantityPerStudent,
      vatPercentage: state.vatPercentage,
    });
    resetForm();
  };

  useEffect(() => {
    if (initialValues) {
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "variantId",
        value: initialValues.variantId || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "variantPrice",
        value: initialValues.price || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "variantForm",
        value: initialValues.formName || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "title",
        value: initialValues.title || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "description",
        value: initialValues.description || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "titleAR",
        value: initialValues.titleAr || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "descriptionAR",
        value: initialValues.descriptionAr || "",
      });

      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "vatEnabled",
        value: initialValues.priceInclusiveOfVat,
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "defaultVariant",
        value: initialValues.defaultVariant,
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "enabledVariant",
        value: initialValues.enabled,
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "variantName",
        value: initialValues.variantName || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "variantCode",
        value: initialValues.variantCode || "",
      });
      // Stock management fields
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "stockControlEnabled",
        value: initialValues.stockControlEnabled || false,
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "availableStock",
        value: initialValues.availableStock || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "purchaseLimitEnabled",
        value: initialValues.purchaseLimitEnabled || false,
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "maxQuantityPerStudent",
        value: initialValues.maxQuantityPerStudent || "",
      });
      dispatch({
        type: ACTION_TYPES.SET_FIELD,
        fieldName: "vatPercentage",
        value: initialValues.vatPercentage || "",
      });
    }
  }, [initialValues]);

  const variantsField = [
    {
      id: "variantName",
      label: MARKETPLACE_CONSTANTS.VARIANT_NAME_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.VARIANT_NAME_LABEL,
      value: state.variantName,
      additionalText: "",
      required: true,
      maxLength: 100,
    },
    {
      id: "variantCode",
      label: MARKETPLACE_CONSTANTS.VARIANT_CODE_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.VARIANT_CODE_LABEL,
      value: state.variantCode,
      additionalText: "",
      required: true,
      maxLength: 50,
    },
    {
      id: "variantPrice",
      label: MARKETPLACE_CONSTANTS.VARIANT_PRICE_LABEL,
      type: "number",
      placeholder: MARKETPLACE_CONSTANTS.VARIANT_PRICE_LABEL,
      value: state.variantPrice,
      additionalText: "",
      required: true,
      maxLength: 6,
    },
    {
      id: "variantForm",
      label: MARKETPLACE_CONSTANTS.VARIANT_FORM_LABEL,
      type: "select",
      placeholder: MARKETPLACE_CONSTANTS.VARIANT_FORM_LABEL,
      value: state.variantForm,
      additionalText: "",
      required: true,
    },
    {
      id: "",
      label: MARKETPLACE_CONSTANTS.VARIANT_DESCRIPTION,
    },
    {
      id: "title",
      label: MARKETPLACE_CONSTANTS.TITLE_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.TITLE_LABEL,
      value: state.title,
      additionalText: "",
      required: true,
      maxLength: 100,
    },
    {
      id: "description",
      label: MARKETPLACE_CONSTANTS.DESCRIPTION_TITLE,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.DESCRIPTION_TITLE,
      value: state.description,
      additionalText: MARKETPLACE_CONSTANTS.DESCRIPTION_SAMPLE,
      required: false,
      maxLength: 200,
    },
    {
      id: "titleAR",
      label: MARKETPLACE_CONSTANTS.TITLE_AR_LABEL,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.TITLE_AR_LABEL,
      value: state.titleAR,
      additionalText: "",
      required: true,
      maxLength: 100,
    },
    {
      id: "descriptionAR",
      label: MARKETPLACE_CONSTANTS.DESCRIPTION_TITLE_AR,
      type: "text",
      placeholder: MARKETPLACE_CONSTANTS.DESCRIPTION_TITLE_AR,
      value: state.descriptionAR,
      additionalText: MARKETPLACE_CONSTANTS.DESCRIPTION_AR_SAMPLE,
      required: false,
      maxLength: 200,
    },
  ];

  const handleFieldChange = (event, maxLength) => {
    const { id, value } = event.target;
    if (maxLength && value.toString().length > maxLength) {
      return;
    }
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: id,
      value: value,
    });
  };

  const handleFieldSelect = (event, fieldName) => {
    const { value } = event.target;
    dispatch({
      type: ACTION_TYPES.SET_FIELD,
      fieldName: fieldName,
      value: value,
    });
  };

  const setError = (fieldName, error) => {
    dispatch({
      type: ACTION_TYPES.SET_ERROR,
      fieldName,
      error,
    });
  };

  const resetForm = () => {
    dispatch({
      type: ACTION_TYPES.RESET_FORM,
    });
  };

  // Stock & Tax Management Handler
  const handleStockTaxChange = (fieldName, value) => {
    switch (fieldName) {
      case "quantityPerProductToggle":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "stockControlEnabled",
          value: !state.stockControlEnabled,
        });
        break;
      case "availableStock":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "availableStock",
          value: value,
        });
        break;
      case "maxQuantityPerStudentToggle":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "purchaseLimitEnabled",
          value: !state.purchaseLimitEnabled,
        });
        break;
      case "maxQuantityPerStudent":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "maxQuantityPerStudent",
          value: value,
        });
        break;
      case "priceInclusiveVatToggle":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "vatEnabled",
          value: !state.vatEnabled,
        });
        break;
      case "vatPercentage":
        dispatch({
          type: ACTION_TYPES.SET_FIELD,
          fieldName: "vatPercentage",
          value: value,
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="specification-box">
      {variantsField.map((field) => {
        if (isEmpty(field.id)) {
          return (
            <Typography
              className="w-100 mb-3"
              variant="subtitle2"
              sx={{ color: colors.custom, textAlign: TEXT_ALIGN_LEFT }}
            >
              {field.label}
            </Typography>
          );
        }
        const fieldAs =  field.type === "select" ? "select" : "input";
        const fieldStyle = field.type === "number" ? { borderRight: "none" } : {}
        const handleOnChange = (e) => {
          field.type === "select"
            ? handleFieldSelect(e, field.id)
            : handleFieldChange(e, field.maxLength)
        }
        return (
          <Fragment key={field.id}>
            <InputGroup
              hasValidation
              className="mx-1 w-100 mb-1"
              controlId={field.id}
            >
              <Form.Floating className="flex-grow-1">
                <Form.Control
                  as={fieldAs}
                  id={field.id}
                  type={field.type}
                  required={field.required}
                  autoComplete="off"
                  placeholder={field.placeholder}
                  value={field.value}
                  maxLength={field.maxLength}
                  onChange={
                    handleOnChange
                  }
                  onSelect={
                    handleOnChange
                  }
                  style={fieldStyle}
                >
                  {field.type === "select" ? (
                    <Fragment>
                      <option selected disabled></option>
                      {formList.length > 0 &&
                        formList.map((form) => (
                          <option key={form.id} value={form.name}>
                            {form.name}
                          </option>
                        ))}
                    </Fragment>
                  ) : null}
                </Form.Control>
                <label htmlFor={field.id}>
                  <span>{field.label}</span>
                  {field.type !== "select" && field.required && (
                    <span style={{ color: colors.warning }}> *</span>
                  )}
                </label>
              </Form.Floating>
              {field.id === "variantPrice" && (
                <Button
                  style={{
                    border: BORDER_SECONDARY,
                    background: "transparent",
                    borderLeft: "none",
                  }}
                >
                  <Typography variant="subtitle2">{currency}</Typography>
                </Button>
              )}
            </InputGroup>
            {state.hasError[field.id] ? (
              <div
                className="mb-3 mx-1 w-100"
                style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
              >
                {state.hasError[field.id]}
              </div>
            ) : (
              <Typography
                variant="subtitle2"
                className="mx-1 mb-3 w-100"
                sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
              >
                {field.additionalText}
              </Typography>
            )}
          </Fragment>
        );
      })}

      {/* Stock & Tax Management */}
      <StockManagement
        stockControlEnabled={state.stockControlEnabled}
        availableStock={state.availableStock}
        purchaseLimitEnabled={state.purchaseLimitEnabled}
        maxQuantityPerStudent={state.maxQuantityPerStudent}
        priceInclusiveVatEnabled={state.vatEnabled}
        vatPercentage={state.vatPercentage}
        onChange={handleStockTaxChange}
        hasError={state.hasError}
      />

      {/* Default Variant */}
      <div className="d-flex w-100 justify-content-between align-items-center mb-4 mx-1">
        <div>
          <Typography variant="h5" className="mb-1">
            {MARKETPLACE_CONSTANTS.DEFAULT_VARIANT}
          </Typography>
          <Typography variant="h4">
            {MARKETPLACE_CONSTANTS.DEFAULT_VARIANT_DETAILS}
          </Typography>
        </div>
        <div className="d-flex flex-wrap" style={{ marginTop: 8 }}>
          <div>
            <ToggleButton
              isChecked={state.defaultVariant}
              onClick={() =>
                dispatch({
                  type: ACTION_TYPES.SET_FIELD,
                  fieldName: "defaultVariant",
                  value: !state.defaultVariant,
                })
              }
              disabled={variantList.length < 2}
            />
          </div>
        </div>
      </div>
      {/* Enabled Variant */}
      <div className="d-flex w-100 justify-content-between align-items-center mb-4 mx-1">
        <div>
          <Typography variant="h5" className="mb-1">
            {MARKETPLACE_CONSTANTS.ENABLED_VARIANT}
          </Typography>
          <Typography variant="h4">
            {MARKETPLACE_CONSTANTS.ENABLED_VARIANT_DETAILS}
          </Typography>
        </div>
        <div className="d-flex flex-wrap" style={{ marginTop: 8 }}>
          <div>
            <ToggleButton
              isChecked={state.enabledVariant}
              onClick={() =>
                dispatch({
                  type: ACTION_TYPES.SET_FIELD,
                  fieldName: "enabledVariant",
                  value: !state.enabledVariant,
                })
              }
            />
          </div>
        </div>
      </div>
      <div className=" d-flex flex-row gap-3 w-100">
        <Button
          className="cancel-button w-100"
          disabled={variantList.length < 2}
          onClick={() => {
            resetForm();
            onDelete();
          }}
        >
          Delete
        </Button>

        <Button
          className="generate-button w-100"
          onClick={handleSave}
          disabled={
            state.hasError.variantPrice ||
            state.hasError.title ||
            state.hasError.titleAR ||
            state.hasError.variantName ||
            state.hasError.variantCode
          }
        >
          Done
        </Button>
      </div>
    </div>
  );
  };

  Variants.propTypes = {
    initialValues: PropTypes.shape({
      variantId: PropTypes.string,
      price: PropTypes.string,
      formName: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      titleAr: PropTypes.string,
      descriptionAr: PropTypes.string,
      priceInclusiveOfVat: PropTypes.bool,
      defaultVariant: PropTypes.bool,
      enabled: PropTypes.bool,
      variantName: PropTypes.string,
      variantCode: PropTypes.string,
      // Stock management fields
      stockControlEnabled: PropTypes.bool,
      availableStock: PropTypes.string,
      purchaseLimitEnabled: PropTypes.bool,
      maxQuantityPerStudent: PropTypes.string,
      vatPercentage: PropTypes.string,
    }),
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    variantList: PropTypes.arrayOf(
      PropTypes.shape({
        variantId: PropTypes.string,
        price: PropTypes.string,
        formName: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        titleAr: PropTypes.string,
        descriptionAr: PropTypes.string,
        priceInclusiveOfVat: PropTypes.bool,
        defaultVariant: PropTypes.bool,
        enabled: PropTypes.bool,
        variantName: PropTypes.string,
        variantCode: PropTypes.string,
        // Stock management fields
        stockControlEnabled: PropTypes.bool,
        availableStock: PropTypes.string,
        purchaseLimitEnabled: PropTypes.bool,
        maxQuantityPerStudent: PropTypes.string,
        vatPercentage: PropTypes.string,
      })
    ),
    currency: PropTypes.string,
    formList: PropTypes.array
  };

  export default Variants;
